import { useMutation, useQueryClient } from "@tanstack/react-query";

import { updateTemplateItem } from "@/adapters/api";
import type { StageTemplate, TemplateItem } from "../types.ts";
import type { SiblingMove } from "../utils/tree.ts";

interface TemplateCache {
	template: StageTemplate;
	items: TemplateItem[];
}

export function useReorderTemplateItem(templateId: string | undefined) {
	const queryClient = useQueryClient();
	const queryKey = ["stage-template", templateId];

	return useMutation({
		mutationFn: async (move: SiblingMove) => {
			if (!templateId) throw new Error("missing template");
			for (const { itemId, position } of move.changes) {
				await updateTemplateItem(templateId, itemId, { position });
			}
		},
		onMutate: async (move) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<TemplateCache>(queryKey);
			if (previous) {
				queryClient.setQueryData<TemplateCache>(queryKey, {
					...previous,
					items: move.items,
				});
			}
			return { previous };
		},
		onError: (_error, _move, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey });
		},
	});
}
